<template>
  <div :class="$style.root">
    <SettingsModal
      ref="settingsModal"
      :currentTab="currentTab"
      :cameras="$store.state.mediaroom.rtcDevices.cameras"
      :microphones="$store.state.mediaroom.rtcDevices.microphones"
      :videoQualityOptions="videoQualityOptions"
      :showVideoQualitySettings="!isRecording"
      :selectedVideoQuality="getSelectedVideoQuality"
      :speakers="$store.state.mediaroom.rtcDevices.speakers"
      :selectedCamera="$store.state.mediaroom.currentDevices.camera"
      :selectedMicrophone="$store.state.mediaroom.currentDevices.microphone"
      :selectedSpeaker="$store.state.mediaroom.currentDevices.speaker"
      :permissions="browserPermissions"
      :errors="errors"
      :active="active"
      :showIcons="false"
      :tabs="tabs"
      :showOnlySpeakerUI="showOnlySpeakerUI"
      :virtualBackgrounds="$store.getters['mediaroom/virtualBackgrounds']"
      :selectedVirtualBackground="$store.state.mediaroom.currentBackgroundFilter"
      :permissionErrorHeader="permissionErrorHeader"
      :deviceErrorHeader="deviceErrorHeader"
      :browser="browserName"
      :dark="
        $store.state.event.event.theme_obj && $store.state.event.event.theme_obj.primary_background_shade === '0, 0, 0'
      "
      :mirror="$store.state.mediaroom.mirrorSelf"
      :noiseCancellationOptions="noiseCancellationOptions"
      :selectedNoiseCancellationLevel="selectedNoiseCancellationLevel"
      :noiseCancellationEnabled="$store.state.mediaroom.noiseCancellationEnabled"
      :noiseCancellationCompatible="$store.state.mediaroom.noiseCancellationCompatible"
      :isFloatingEmojiReactionsEnabled="isFloatingEmojiReactionsEnabled"
      :floatingEmojiVisible="floatingEmojiVisible"
      @changeNoiseCancellationLevel="level => $store.dispatch('mediaroom/changeNoiseCancellationLevel', level.value)"
      @toggleMirror="() => $store.commit('mediaroom/toggleMirrorSelf')"
      @cancel="$emit('cancel')"
      @openSystemLink="openSystemLink"
      @selectDevice="selectDevice"
      @selectVideoQuality="selectVideoQuality"
      @tab="tab = $event"
      @setVirtualBackground="bg => $store.dispatch('mediaroom/setVirtualBackground', bg)"
      @toggleNoiseCancellation="
        $store.dispatch('mediaroom/toggleNoiseCancellation', !$store.state.mediaroom.noiseCancellationEnabled)
      "
      @toggleFloatingEmoji="
        $store.commit('event/setHideFloatingEmojiReactions', !$store.state.event.hideFloatingEmojiReactions)
      "
    />
  </div>
</template>

<script>
import { appMode } from '@/assets/utilfunctions';
import { AGORA, NOISE_SUPPRESSION_DROPDOWN_OPTIONS } from '@/constants';
import { getCurrentAutoQuality } from '@/services/agoraVideoQuality.service';
import { getBrowserSpecs, openSystemSoundSettings } from '@/services/browser.service';
import { onStorageEvent, getValueFromLocalstore } from '@/services/localStorage.service';
import loggerService from '@/services/logger.service';
import { registerAudioLevelCallback, unregisterAudioLevelCallback } from '@/services/stream.service';

export default {
  name: 'AudioVideoSettingsModal',
  props: {
    startWithTab: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      tab: null,
      browserName: '',
      tvSpeaker: '',
      noiseCancellationOptions: NOISE_SUPPRESSION_DROPDOWN_OPTIONS
    };
  },
  unmounted() {
    unregisterAudioLevelCallback(this.updateAudioLevel);
  },
  mounted() {
    if (this.startWithTab && ['audio', 'video', 'virtualBackground', 'floatingReactions'].includes(this.startWithTab)) {
      this.tab = this.startWithTab;
    }
    registerAudioLevelCallback(this.updateAudioLevel.bind(this));
    onStorageEvent(e => {
      this.tvSpeaker = JSON.parse(e.newValue);
    }, 'tvSpeaker');
    this.browserName = getBrowserSpecs()?.name;
    this.tvSpeaker = getValueFromLocalstore('tvSpeaker');
  },
  computed: {
    getSelectedVideoQuality() {
      let selectedQuality;

      if (this.$store.state.user.selected_video_quality.name) {
        selectedQuality = this.$store.state.user.selected_video_quality;
      } else if (this.$store.state.event.event?.video_quality) {
        selectedQuality = AGORA.VIDEO_QUALITY.find(vq => vq.name === this.$store.state.event.event.video_quality);
      } else {
        selectedQuality = AGORA.VIDEO_QUALITY[0];
      }

      const channelName = this.getCurrentChannelName();
      const isOnBackstage = channelName?.includes(AGORA.UID_SUFFIX.BACKSTAGE);

      // If user is on backstage, always show max 480p in dropdown, even if previously selected higher quality on main stage
      if (isOnBackstage && selectedQuality?.name !== 'AUTO') {
        const idx = AGORA.VIDEO_QUALITY.findIndex(q => q.name === selectedQuality.name);
        const idx480 = AGORA.VIDEO_QUALITY.findIndex(q => q.name === 'REGULAR');
        if (idx > idx480) {
          selectedQuality = AGORA.VIDEO_QUALITY[idx480];
        }
      }

      // If Auto is selected, enhance it with current quality like we do in videoQualityOptions
      if (selectedQuality?.name === 'AUTO') {
        if (channelName) {
          try {
            const currentQuality = getCurrentAutoQuality(channelName);
            if (currentQuality) {
              const qualityLabel = this.getQualityLabel(currentQuality.name);
              return {
                ...selectedQuality,
                label: `Auto (${qualityLabel})`
              };
            }
          } catch (error) {
            loggerService.warn('Error getting current auto quality for selected:', error);
          }
        }
      }

      return selectedQuality;
    },
    videoQualityOptions() {
      const channelName = this.getCurrentChannelName();
      const useFullHD = this.$store.getters['event/useFullHD'];
      const isOnBackstage = channelName && channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE);

      const allOptions = AGORA.VIDEO_QUALITY.map(option => {
        const enhancedOption = { ...option };

        // If this is the Auto option, only show current quality in brackets when Auto is selected
        if (option.name.toLocaleLowerCase() === 'auto') {
          // Check if Auto is currently selected
          const isAutoSelected =
            this.$store.state.user.selected_video_quality.name === 'AUTO' ||
            (!this.$store.state.user.selected_video_quality.name &&
              (!this.$store.state.event.event?.video_quality ||
                this.$store.state.event.event.video_quality === 'AUTO'));

          if (isAutoSelected && channelName) {
            try {
              const currentQuality = getCurrentAutoQuality(channelName);
              if (currentQuality) {
                const qualityLabel = this.getQualityLabel(currentQuality.name);
                enhancedOption.label = `Auto (${qualityLabel})`;
              }
            } catch (error) {
              loggerService.warn('Error getting current auto quality:', error);
            }
          }
        }
        return enhancedOption;
      });

      let filteredOptions = allOptions;

      // Filter out 1080p if not enabled
      if (!useFullHD) {
        filteredOptions = filteredOptions.filter(option => option.name !== 'ULTRA_HIGH');
      }

      // Filter out 720p and 1080p completely when on backstage
      if (isOnBackstage) {
        filteredOptions = filteredOptions.filter(option => option.name !== 'HIGH' && option.name !== 'ULTRA_HIGH');
      }

      return filteredOptions;
    },
    errors() {
      return {
        ...this.$store.state.mediaroom.rtcDeviceAccessError,
        speakerOverlap: this.speakerOverlap
      };
    },
    systemPermissions() {
      return this.$store.state.mediaroom.rtcDeviceSystemPermission;
    },
    browserPermissions() {
      return this.$store.state.mediaroom.rtcDeviceBrowserPermission;
    },
    rtcDeviceAccessError() {
      return this.$store.state.mediaroom.rtcDeviceAccessError;
    },
    isRecording() {
      return this.$store.getters['event/isRecording'];
    },
    isFloatingEmojiReactionsEnabled() {
      return this.$store.getters['event/isFloatingEmojiReactionsEnabled'];
    },
    floatingEmojiVisible() {
      return !this.$store.state.event.hideFloatingEmojiReactions;
    },
    showVirtualBackgrounds() {
      if (this.isRecording) {
        return false;
      }

      if (!this.showCameraSettings) {
        return false;
      }

      return this.$store.state.mediaroom.allowVirtualBackgrounds;
    },
    isOrganizerOrSpeaker() {
      return this.$store.getters['event/isOrganizerOrSpeaker'];
    },
    showCameraSettings() {
      if (appMode === 'tv') {
        return false;
      }

      if (this.isOrganizerOrSpeaker) {
        return true;
      }

      return (
        !this.systemPermissions?.camera ||
        !this.browserPermissions?.camera ||
        !!this.$store.state.mediaroom.rtcDevices.cameras?.length
      );
    },
    showOnlySpeakerUI() {
      if (appMode === 'tv') {
        return true;
      }

      if (this.isOrganizerOrSpeaker) {
        return false;
      }

      if (!this.systemPermissions?.microphone || !this.browserPermissions?.microphone) {
        return false;
      }

      return !this.$store.state.mediaroom.rtcDevices.microphones?.length;
    },
    tabs() {
      return [
        {
          id: 'audio',
          label: '@@translate@@ Audio',
          icon: 'volume',
          error: this.errors?.microphone || !this.browserPermissions?.microphone || this.errors?.speakerOverlap
        },
        ...(this.showCameraSettings
          ? [
              {
                id: 'video',
                label: '@@translate@@ Video',
                icon: 'videocam',
                error: this.errors?.camera || !this.browserPermissions?.camera
              }
            ]
          : []),
        ...(this.showVirtualBackgrounds
          ? [
              {
                id: 'virtualBackground',
                label: '@@translate@@ Virtual Backgrounds',
                icon: 'background'
              }
            ]
          : []),
        ...(this.isFloatingEmojiReactionsEnabled
          ? [
              {
                id: 'floatingReactions',
                label: '@@translate@@ Floating Reactions',
                icon: 'smiley'
              }
            ]
          : [])
      ];
    },
    permissionErrorHeader() {
      const isCameraPermissionGiven = this.showCameraSettings ? this.browserPermissions?.camera : true;

      const isMicPermissionGiven = this.browserPermissions?.microphone;

      if (!isCameraPermissionGiven && !isMicPermissionGiven) {
        return {
          title: '@@translate@@ Goldcast needs access to your camera and microphone',
          icon: 'videocam-muted',
          type: 'both'
        };
      }
      if (!isMicPermissionGiven) {
        return {
          title: '@@translate@@ Goldcast needs access to your microphone',
          icon: 'muted',
          type: 'microphone'
        };
      }
      if (!isCameraPermissionGiven) {
        return {
          title: '@@translate@@ Goldcast needs access to your camera',
          icon: 'videocam-muted',
          type: 'camera'
        };
      }

      return null;
    },
    deviceErrorHeader() {
      const hasCameraError = this.showCameraSettings ? this.rtcDeviceAccessError.camera : false;
      const hasMicError = this.rtcDeviceAccessError.microphone;

      if (hasCameraError && hasMicError) {
        return {
          icon: 'muted',
          type: 'both',
          title: '@@translate@@ Please choose another microphone and camera',
          text: '@@translate@@ The currently selected microphone and camera are not working. Please choose another microphone and camera from the dropdowns below.'
        };
      }
      if (hasCameraError) {
        return {
          icon: 'videocam-muted',
          type: 'camera',
          title:
            '@@translate@@ The currently selected camera is not working. Please choose another camera in the dropdown below.',
          text: '@@translate@@ You are welcome to continue without a camera however we recommend resolving the conflict to ensure a smooth broadcast experience'
        };
      }
      if (hasMicError) {
        return {
          icon: 'muted',
          type: 'microphone',
          title: '@@translate@@ Please choose another microphone',
          text: '@@translate@@ The currently selected microphone is not working. Please choose another microphone in the dropdown below.'
        };
      }
      if (this.speakerOverlap) {
        return {
          icon: 'faunmute',
          title: '@@translate@@ Please check that your TV speaker is correct',
          text: '@@translate@@ The currently selected speaker is not working. Please choose another speaker in the dropdown below.'
        };
      }
      return null;
    },
    currentTab() {
      if (this.tab) {
        return this.tab;
      }

      const errorType = this.permissionErrorHeader || this.deviceErrorHeader;

      if (['both', 'camera'].includes(errorType?.type)) {
        return 'video';
      }

      return 'audio';
    },
    controls() {
      const getControlStatus = type => {
        return {
          isMuted: !this.$store.state.mediaroom.stageControls?.[type]
        };
      };
      return {
        audio: getControlStatus('audio'),
        video: getControlStatus('video')
      };
    },
    active() {
      return {
        camera: !this.controls.video.isMuted,
        microphone: !this.controls.audio.isMuted,
        speaker: true
      };
    },
    speakerOverlap() {
      return (
        this.$store.state.event.stage.tvWindow &&
        !this.$store.state.event.stage.isTVMode &&
        !this.$store.state.event.stage.isLiveStreamMode &&
        this.tvSpeaker?.deviceId === this.$store.state.mediaroom.currentDevices.speaker?.deviceId
      );
    },
    selectedNoiseCancellationLevel() {
      return this.noiseCancellationOptions.find(
        option => option.value === this.$store.state.mediaroom.selectedNoiseSuppressionLevel
      );
    }
  },
  methods: {
    getCurrentChannelName() {
      const broadcastId = this.$store.getters['event/currentBroadcast']?.id;
      if (!broadcastId) {
        return null;
      }

      // In case of simulive mainstage is never created
      const mainStage = this.$store.state.mediaroom.mediaRooms[broadcastId];
      const isConnectedToBackstage = mainStage ? mainStage.userRole === 'audience' : true;

      return isConnectedToBackstage ? broadcastId + AGORA.UID_SUFFIX.BACKSTAGE : broadcastId;
    },

    getQualityLabel(qualityName) {
      return AGORA.VIDEO_QUALITY.find(quality => quality.name === qualityName)?.label;
    },
    updateAudioLevel(audioLevel) {
      let audioLevelHeight = audioLevel;
      if (this.controls.audio.isMuted) {
        audioLevelHeight = 0;
      }
      if (this.$refs.settingsModal?.$refs?.audioSettings?.$refs?.audioIndicator?.setLevel) {
        this.$refs.settingsModal.$refs.audioSettings.$refs.audioIndicator.setLevel(audioLevelHeight);
      }
    },
    selectDevice(type, device) {
      if (device && device.deviceId !== this.$store.state.mediaroom.currentDevices[device]?.deviceId) {
        this.$store.dispatch('mediaroom/switchDevice', {
          deviceTag: type,
          device: device
        });
      }
    },
    selectVideoQuality(videoQuality) {
      loggerService.log('selectVideoQuality', videoQuality);
      this.$store.dispatch('mediaroom/switchVideoQuality', videoQuality);
    },
    openSystemLink() {
      openSystemSoundSettings();
    }
  }
};
</script>

<style module>
.root {
  display: flex;
  justify-content: center;
  align-items: safe center;
  height: 100%;
  max-block-size: 100dvh;
  min-inline-size: 0;
  max-inline-size: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.root > * {
  min-inline-size: 0 !important;
  max-inline-size: 100% !important;
  overflow-wrap: anywhere;
}
</style>
